import { throttle } from "./utils";

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: block;
      position: sticky;
      top: 0;
      transform: translateY(0);
      z-index: 1000;
    }

    :host([offscreen]) {
      transform: translateY(-100%);
    }

    :host([is-in-opulent-area]) {
      background-color: transparent;
    }

    :host([no-transition]) {
      transition: none !important;
    }

    @media (prefers-reduced-motion: no-preference) {
      :host {
        transition:
          transform 600ms ease-out;
      }
    }
  </style>
  <slot></slot>
`;

export class StickyHeader extends HTMLElement {
  #isOnOpulentPage: boolean;
  #minimalThreshold: number;
  #opulentAreaHeight: number;
  #pageOpulentArea: HTMLElement | null;
  #previousScroll: number;
  #scrollDirection: "up" | "down" | undefined;
  #scrollDownCumulation: number;
  #scrollThreshold: number;
  #scrollUpCumulation: number;
  #scrollUpThreshold: number;

  get isInOpulentArea() {
    return this.hasAttribute("is-in-opulent-area");
  }

  set isInOpulentArea(value: boolean) {
    value
      ? this.setAttribute("is-in-opulent-area", "")
      : this.removeAttribute("is-in-opulent-area");
  }

  get minimal() {
    return this.hasAttribute("minimal");
  }

  set minimal(value: boolean) {
    value ? this.setAttribute("minimal", "") : this.removeAttribute("minimal");
  }

  get noTransition() {
    return this.hasAttribute("no-transition");
  }

  set noTransition(value: boolean) {
    value
      ? this.setAttribute("no-transition", "")
      : this.removeAttribute("no-transition");
  }

  get offscreen() {
    return this.hasAttribute("offscreen");
  }

  set offscreen(value: boolean) {
    value
      ? this.setAttribute("offscreen", "")
      : this.removeAttribute("offscreen");
  }

  get opulentAreaSelector() {
    return this.getAttribute("opulent-area-selector");
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));
    this.#isOnOpulentPage = false;
    this.#minimalThreshold = 12;
    this.#opulentAreaHeight = 0;
    this.#pageOpulentArea = null;
    this.#previousScroll = 0;
    this.#scrollDirection = undefined;
    this.#scrollDownCumulation = 0;
    this.#scrollThreshold = 80;
    this.#scrollUpCumulation = 0;
    this.#scrollUpThreshold = 64;
  }

  connectedCallback() {
    // initially disable transitions to avoid flashing of header on reload of scrolled page
    this.noTransition = true;
    this.#pageOpulentArea = this.opulentAreaSelector
      ? document.querySelector(this.opulentAreaSelector)
      : null;

    if (this.#pageOpulentArea) {
      this.#isOnOpulentPage = true;
      this.#opulentAreaHeight =
        this.#pageOpulentArea.clientHeight + this.clientHeight;
      this.isInOpulentArea = window.scrollY <= this.#opulentAreaHeight;
    }

    this.offscreen = window.scrollY > this.#scrollThreshold;

    window.addEventListener("scroll", this.#throttledScrollHandler, {
      passive: true,
    });
  }

  #scrollHandler = () => {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this.noTransition = false;
    }
    const currentScroll = window.scrollY;

    if (currentScroll <= 0) {
      this.#scrollDirection = undefined;
      this.offscreen = false;
      this.minimal = false;
      return;
    }

    this.isInOpulentArea =
      this.#isOnOpulentPage && currentScroll <= this.#opulentAreaHeight;

    this.minimal = window.scrollY > this.#minimalThreshold;

    this.#scrollDirection =
      currentScroll < this.#previousScroll ? "up" : "down";

    if (this.#scrollDirection === "up") {
      this.#scrollDownCumulation = 0;
      this.#scrollUpCumulation += this.#previousScroll - currentScroll;
    } else if (this.#scrollDirection === "down") {
      this.#scrollDownCumulation += currentScroll - this.#previousScroll;
      this.#scrollUpCumulation = 0;
    }

    if (
      this.#scrollDirection === "down" &&
      currentScroll > this.#scrollThreshold &&
      this.#scrollDownCumulation > this.#scrollThreshold
    ) {
      this.offscreen = true;
    }

    if (
      this.#scrollDirection === "up" &&
      this.#scrollUpCumulation > this.#scrollUpThreshold
    ) {
      const newOffscreenState =
        this.#isOnOpulentPage &&
        currentScroll <= this.#opulentAreaHeight + this.clientHeight &&
        currentScroll > this.#scrollThreshold;
      if (newOffscreenState !== this.offscreen) {
        this.offscreen = newOffscreenState;
      }
    }

    this.#previousScroll = currentScroll;
  };

  #throttledScrollHandler = throttle(this.#scrollHandler, 100);

  disconnectedCallback() {
    window.removeEventListener("scroll", this.#throttledScrollHandler);
  }
}

"customElements" in window &&
  customElements.get("ws-sticky-header") === undefined &&
  customElements.define("ws-sticky-header", StickyHeader);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-sticky-header": StickyHeader;
  }
}
